// Event promos — the deals on one event (migration 0035).

import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database';
import type { EventPromo, PromoItem, PromoKind } from '@/types/domain';

type PromoRow = {
  id: string; event_id: string; kind: PromoKind; name: string; is_active: boolean;
  config: Json; sort: number;
  event_promo_items?: { product_id: string; qty: number; buy_qty: number | null; get_qty: number | null }[] | null;
};

function mapPromo(r: PromoRow): EventPromo {
  return {
    id: r.id,
    eventId: r.event_id,
    kind: r.kind,
    name: r.name,
    isActive: r.is_active,
    config: (r.config && typeof r.config === 'object' && !Array.isArray(r.config)
      ? (r.config as Record<string, unknown>)
      : {}),
    sort: r.sort,
    items: (r.event_promo_items ?? []).map((i) => ({
      productId: i.product_id,
      qty: i.qty,
      buyQty: i.buy_qty ?? undefined,
      getQty: i.get_qty ?? undefined,
    })),
  };
}

export async function listEventPromos(eventId: string): Promise<EventPromo[]> {
  const { data, error } = await supabase
    .from('event_promos')
    .select('*, event_promo_items(product_id, qty, buy_qty, get_qty)')
    .eq('event_id', eventId)
    .order('sort')
    .order('created_at');
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapPromo(r as unknown as PromoRow));
}

export interface SavePromoInput {
  id?: string;
  eventId: string;
  kind: PromoKind;
  name: string;
  isActive?: boolean;
  config: Record<string, unknown>;
  items: PromoItem[];
}

/** Insert or update a promo and replace its item list. */
export async function savePromo(input: SavePromoInput): Promise<string> {
  const { data: auth } = await supabase.auth.getUser();
  let id = input.id;
  if (id) {
    const { error } = await supabase
      .from('event_promos')
      .update({ name: input.name.trim(), is_active: input.isActive ?? true, config: input.config as Json })
      .eq('id', id);
    if (error) throw new Error(error.message);
    const { error: delErr } = await supabase.from('event_promo_items').delete().eq('promo_id', id);
    if (delErr) throw new Error(delErr.message);
  } else {
    const { data, error } = await supabase
      .from('event_promos')
      .insert({
        event_id: input.eventId,
        kind: input.kind,
        name: input.name.trim(),
        is_active: input.isActive ?? true,
        config: input.config as Json,
        created_by: auth.user?.id ?? null,
      })
      .select('id')
      .single();
    if (error) throw new Error(error.message);
    id = data.id;
  }
  if (input.items.length > 0) {
    const { error } = await supabase.from('event_promo_items').insert(
      input.items.map((i) => ({
        promo_id: id!,
        product_id: i.productId,
        qty: Math.max(1, Math.floor(i.qty)),
        buy_qty: input.kind === 'bogo' ? Math.max(1, Math.floor(i.buyQty ?? 1)) : null,
        get_qty: input.kind === 'bogo' ? Math.max(1, Math.floor(i.getQty ?? 1)) : null,
      })),
    );
    if (error) throw new Error(error.message);
  }
  return id!;
}

export async function setPromoActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase.from('event_promos').update({ is_active: isActive }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Refused by the database while an invoice line still points at the promo. */
export async function deletePromo(id: string): Promise<void> {
  const { error } = await supabase.from('event_promos').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
